"use client";

import React from "react";
import { Download, ShieldCheck, FileText, CircleCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const standards = [{
  id: "item-1",
  code: "NBR 16401",
  title: "Instalações de Ar-Condicionado - Sistemas Centrais e Unitários",
  description: "A NBR 16401 estabelece os parâmetros de conforto térmico e qualidade do ar interior, essenciais para projetos de climatização. Na Doctorice, seguimos rigorosamente esta norma para garantir eficiência energética, controle de temperatura preciso e renovação de ar adequada em ambientes comerciais e industriais."
}, {
  id: "item-2",
  code: "ISO 14644",
  title: "Salas Limpas e Ambientes Controlados",
  description: "Fundamental para indústrias farmacêuticas e eletrônicas, a ISO 14644 classifica a pureza do ar por concentração de partículas. Nossos projetos de salas limpas asseguram a conformidade com as classes ISO 5 a ISO 8, integrando filtragem HEPA/ULPA, controle de pressão diferencial e fluxos de ar laminares ou turbulentos conforme a necessidade do processo."
}, {
  id: "item-3",
  code: "ANVISA RDC 50",
  title: "Projetos Físicos de Estabelecimentos de Saúde",
  description: "A RDC 50 define os critérios para planejamento e elaboração de projetos em ambientes de saúde. Atuamos com foco total na segurança do paciente e controle de infecção hospitalar, projetando sistemas de climatização que atendem aos requisitos críticos de áreas como centros cirúrgicos, UTIs e laboratórios."
}, {
  id: "item-4",
  code: "PMOC - Lei 13.589/2018",
  title: "Plano de Manutenção, Operação e Controle",
  description: "O PMOC é obrigatório para edifícios de uso público e coletivo. A Doctorice não apenas implementa o PMOC, mas utiliza-o como ferramenta de gestão para prolongar a vida útil dos equipamentos, reduzir custos operacionais e garantir a qualidade do ar interior, evitando a 'Síndrome do Edifício Doente'."
}];
export default function NormasECertificacoes() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="normas-e-certificações" className="relative w-full py-20 bg-background overflow-hidden" style={{
    "--primary": "hsl(180 100% 30%)",
    "--primary-foreground": "hsl(0 0% 100%)",
    "--secondary": "hsl(165 30% 30%)",
    "--foreground": "hsl(185 40% 12%)",
    "--muted": "hsl(165 30% 95%)",
    "--muted-foreground": "hsl(185 40% 12%)",
    "--border": "hsl(165 30% 85%)",
    "--ring": "hsl(180 100% 30%)",
    "--card": "hsl(0 0% 100%)"
  } as React.CSSProperties}>
      <style dangerouslySetInnerHTML={{
      __html: `
        #normas-e-certificações.dark {
          --primary: hsl(180 100% 60%);
          --secondary: hsl(165 30% 20%);
          --foreground: hsl(185 40% 92%);
          --muted: hsl(165 30% 12%);
          --border: hsl(165 30% 30%);
        }
      `
    }} />
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]">
        <svg className="w-full h-full" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-pattern)" />
        </svg>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={ref} className={`max-w-4xl mx-auto transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="text-center mb-12">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/doctorice_technical_logo.png" alt="DOCTORICE" className="h-10 mx-auto mb-6" />
            <div className="inline-flex items-center justify-center p-2 bg-accent/10 rounded-full mb-4">
              <ShieldCheck className="w-5 h-5 text-primary mr-2" />
              <span className="text-sm font-medium text-primary tracking-wide uppercase">
                Conformidade Técnica
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
              Normas e Certificações
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Nossa engenharia é fundamentada no rigor técnico. Conheça as
              principais normas que regem nossos projetos e garantem a segurança
              e eficiência das suas instalações.
            </p>
          </div>

          {}
          <div className="bg-card border border-border/50 rounded-xl shadow-sm p-6 md:p-8 mb-10">
            <Accordion type="single" collapsible className="w-full">
              {standards.map((item, i) => <AccordionItem key={item.id} value={item.id} className="border-b-border/60 last:border-0" data-index={i}>
                  <AccordionTrigger className="hover:no-underline py-5 group">
                    <div className="flex items-center text-left gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/5 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                        <FileText className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <span className="block text-xs font-bold text-primary uppercase tracking-wider mb-0.5">
                          {item.code}
                        </span>
                        <span className="text-base md:text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                          {item.title}
                        </span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base leading-relaxed pl-[3.5rem] pr-4 pb-6">
                    <div className="flex gap-3">
                      <div className="w-0.5 bg-primary/20 self-stretch rounded-full" />
                      <p>{item.description}</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {}
          <div className="flex flex-col md:flex-row items-center justify-between bg-primary/5 rounded-lg p-6 border border-primary/10 gap-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-background rounded-full shadow-sm text-primary mt-1">
                <CircleCheck className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-lg">
                  Documentação Técnica Completa
                </h3>
                <p className="text-muted-foreground text-sm mt-1">
                  Baixe nosso guia detalhado sobre normas aplicadas a ambientes
                  críticos.
                </p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <Button asChild size="lg" className="w-full md:w-auto shadow-md">
                <Link to="#" download>
                  <Download className="mr-2 h-4 w-4" />
                  Baixar Guia de Normas
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}